import { ArrowRight, ListChecks, Plus, Search, Settings2, Sparkles, X } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { EmptyState, Spinner } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/misc";
import { useAuth } from "@/lib/auth";
import {
  acceptSuggestions, acceptableCount, addKey, buildRows, clearRow, fieldSources, filterRows, keyNotes, keyProblem,
  mapperCounts, mapperFields, mappingFromRows, pickField, requestState, stableJson, standardUses, withUses,
  type Confidence, type FieldSource, type MapperFilter, type MapperRow, type RequestState,
} from "@/lib/field-mapping";
import { fieldTypeLabel } from "@/lib/forms";
import { useFormDesign, useTriggerInbox, useTriggerRequest } from "@/lib/queries";
import type { AdminForm, Trigger } from "@/lib/types";
import { cn } from "@/lib/utils";

const CONFIDENCE: Record<Confidence, string> = {
  exact: "The field's title is this key.",
  close: "The same words, once underscores, punctuation and capitals are ignored.",
  likely: "Most of the words are the same. Check it before you take it.",
};

function plural(count: number, noun: string): string {
  return count === 1 ? `1 ${noun}` : `${count} ${noun}s`;
}

/**
 * The trigger page's Custom fields card (ADR-119 in platform): how many of the newest request's keys
 * fill one of the contact's custom fields, what is left, and the way to the trigger's Edit, where the
 * mapping is changed. Only a trigger that makes leads has one.
 */
export function TriggerFieldsCard({ trigger, onEdit }: { trigger: Trigger; onEdit: () => void }) {
  const { user } = useAuth();
  // The trigger's tenant, which a superadmin names; everyone else's is their own.
  const design = useFormDesign("lead", user?.role === "superadmin" ? trigger.tenant_id : null);
  const inbox = useTriggerInbox(trigger.id);
  const newestId = inbox.data?.items[0]?.id;
  const newest = useTriggerRequest(trigger.id, newestId);
  const state = requestState({
    failed: (inbox.isError && inbox.data === undefined) || (newest.isError && newest.data === undefined),
    count: inbox.data?.items.length,
    raw: newest.data ? newest.data.raw : undefined,
  });
  const doc = state === "read" ? (newest.data?.raw ?? null) : null;
  const rows = React.useMemo(
    () => buildRows(doc, fieldSources(trigger.mapping?.fields), standardUses(trigger), design.data),
    [doc, trigger, design.data],
  );
  const compared = state === "read";
  const counts = mapperCounts(rows, compared);
  const loading = design.isPending || state === undefined;

  return (
    <Card>
      <CardHeader
        title="Custom fields"
        description={
          trigger.action === "lead.update"
            ? "Answers copied from the request into the contact's fields, for a lead the platform already has: each key the sender posts goes into the field picked for it, under Edit."
            : "A new lead's answers, copied from the request into the contact's fields: each key the sender posts goes into the field picked for it, under Edit."
        }
        action={
          <Button variant="outline" onClick={onEdit}>
            <Settings2 />
            Edit the mapping
          </Button>
        }
      />
      <CardBody className="space-y-3">
        {loading ? (
          <Spinner className="size-4" />
        ) : (
          <dl className="flex flex-wrap gap-x-8 gap-y-2">
            <Stat value={counts.mapped} label="mapped" />
            {compared && (
              <>
                <Stat value={counts.unmapped} label="not mapped" tone={counts.unmapped > 0 ? "warning" : undefined} />
                <Stat value={counts.missing} label="not in the newest request" tone={counts.missing > 0 ? "warning" : undefined} />
                <Stat value={counts.own} label="read by the trigger itself" />
              </>
            )}
          </dl>
        )}
        {!loading && state !== "read" && <p className="text-xs text-text-muted">{REQUEST_NOTES[state ?? "none"]}</p>}
        {counts.unmapped > 0 && (
          <p className="text-xs text-text-muted">
            {plural(counts.unmapped, "key")} in the newest request {counts.unmapped === 1 ? "fills" : "fill"} no field
            {counts.suggested > 0 ? `, ${counts.suggested} with a suggested field` : ""}.
          </p>
        )}
        {counts.missing > 0 && (
          <p className="text-xs text-text-muted">
            {plural(counts.missing, "mapped key")} {counts.missing === 1 ? "was" : "were"} not in the newest request:
            renamed or removed at the sender.
          </p>
        )}
        <p className="text-xs text-text-subtle">
          A field that is not on the contact form yet is added under{" "}
          <Link to="/custom-fields" className="text-accent-11 hover:underline">
            Custom fields
          </Link>{" "}
          first.
        </p>
      </CardBody>
    </Card>
  );
}

/** The same, inside Edit, where the mapped keys are listed as saved and keys can be typed by hand. */
const MAPPER_NOTES: Record<Exclude<RequestState, "read">, string> = {
  none: "Nothing received yet: the mapped keys are listed as saved.",
  gone: "The newest request's body is no longer kept, so its keys are not listed: the mapped keys are listed as saved. The next request lists its keys again.",
  unreadable: "The newest request could not be read, so its keys are not listed: the mapped keys are listed as saved, and keys can be added by hand below.",
};

/** Why the card and the mapper cannot set the mapping beside the newest request's keys. */
const REQUEST_NOTES: Record<Exclude<RequestState, "read">, string> = {
  none: "Nothing received yet. Send this trigger one request and its keys appear under Edit, or type the keys by hand there.",
  gone:
    "The newest request's body is no longer kept (requests are kept for a set time, 30 days by default), so its keys are not set beside the mapping. The next request's are.",
  unreadable: "The newest request could not be read just now, so its keys are not set beside the mapping.",
};

function Stat({ value, label, tone }: { value: number; label: string; tone?: "warning" }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dd className={cn("text-xl font-semibold tabular-nums", tone === "warning" && value > 0 ? "text-warning-11" : "text-text")}>
        {value}
      </dd>
      <dt className="text-xs text-text-muted">{label}</dt>
    </div>
  );
}

/**
 * Map a request's keys onto the contact's custom fields, inside the trigger form (ADR-119 in platform).
 * The rows are the newest request's keys in the sender's order; each gets a field from a searchable
 * picker, or takes the one suggested for it, and one button takes every sure suggestion. A mapped key
 * the newest request did not carry is listed as such, to re-point or drop, and a key can be typed by
 * hand for a sender that has not posted yet.
 *
 * Controlled by the form: `value` is its `fields` entry, and every edit is reported through `onChange`
 * as the entry it makes (`mappingFromRows`), so the form's own Save carries it with everything else.
 * The rows are read from `doc` and `design` once they are there, and again only when the form resets
 * `value` from outside; `uses` (the keys the form's other paths read) is applied on every render, so a
 * path typed above takes its key out of the custom fields at once.
 */
export function FieldMapper({
  value, onChange, original, doc, request, design, uses,
}: {
  value: Record<string, FieldSource>;
  onChange: (next: Record<string, FieldSource>) => void;
  /** The entry as loaded, so a pairing that did not change keeps its stored form. */
  original: Record<string, FieldSource>;
  /** The newest request's body: null when there is none to read, undefined while it is still being read. */
  doc: Record<string, unknown> | null | undefined;
  /** Why there is no body, when there is none: nothing received (the default for a null `doc`), a body
   *  the retention period emptied, or a request that could not be read. */
  request?: RequestState;
  design: AdminForm | undefined;
  uses: Record<string, string>;
}) {
  const fields = React.useMemo(() => mapperFields(design), [design]);
  const [rows, setRows] = React.useState<MapperRow[]>([]);
  const [filter, setFilter] = React.useState<MapperFilter>("all");
  const [search, setSearch] = React.useState("");
  const [newKey, setNewKey] = React.useState("");
  const [keyError, setKeyError] = React.useState<string | null>(null);
  // The entry the rows last reported; a `value` that is not it was set from outside, so the rows start over.
  const reported = React.useRef<string | null>(null);
  const state: RequestState | undefined = doc === undefined ? undefined : (request ?? (doc === null ? "none" : "read"));
  const ready = state !== undefined && design !== undefined;
  // Only a body in hand says which mapped keys the sender stopped posting.
  const compared = state === "read";
  // The rows start over when the entry is set from outside, or when the request's state changes (a read
  // that failed and then worked), from the entry as it stands, so nothing mapped is lost.
  const tag = (mapping: Record<string, FieldSource>) => `${stableJson(mapping)}|${state}`;

  React.useEffect(() => {
    if (!ready) return;
    const incoming = tag(value);
    if (reported.current === incoming) return;
    reported.current = incoming;
    setRows(buildRows(compared ? doc : null, value, uses, design));
    // `uses` is applied on render; only a value set from outside, or the request and design arriving,
    // starts the rows over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, doc, state, design, value]);

  const shown = React.useMemo(() => withUses(rows, uses, fields), [rows, uses, fields]);

  const change = (next: MapperRow[]) => {
    const mapping = mappingFromRows(next, original);
    reported.current = tag(mapping);
    setRows(next);
    onChange(mapping);
  };

  const counts = mapperCounts(shown, compared);
  const acceptable = acceptableCount(shown);
  const tab: MapperFilter = !compared && filter === "missing" ? "all" : filter;
  const visible = filterRows(shown, tab, search);
  const titles = React.useMemo(() => new Map(fields.map((field) => [field.key, field.title])), [fields]);
  const options: ComboboxOption[] = React.useMemo(
    () =>
      fields.map((field) => ({
        value: field.key,
        label: field.title,
        hint: `${field.qualified} · ${fieldTypeLabel(field.type)}`,
        detail: `${field.qualified} · ${field.section}`,
        group: `Contact › ${field.section}`,
        keywords: `${field.key} ${field.section}`,
      })),
    [fields],
  );

  const add = () => {
    const bad = keyProblem(newKey, shown);
    if (bad) {
      setKeyError(bad);
      return;
    }
    change(addKey(shown, newKey, fields));
    setNewKey("");
    setKeyError(null);
    setFilter("all");
  };

  if (!ready) {
    return (
      <div className="flex items-center gap-2 text-xs text-text-muted">
        <Spinner className="size-3.5" />
        Reading the newest request and the contact form.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="trigger-field-mapper">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label="Search keys and fields"
          placeholder="Search keys and fields"
          leading={<Search />}
          className="sm:max-w-xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto"
          disabled={acceptable === 0}
          title="Takes every suggestion whose title is this key, or the same words. The likely ones are left for you."
          onClick={() => change(acceptSuggestions(shown, fields))}
        >
          <Sparkles />
          Map {acceptable} suggested
        </Button>
      </div>

      {state !== undefined && state !== "read" && (state !== "none" || shown.length > 0) && (
        <p className="text-xs text-text-muted" role="status">
          {MAPPER_NOTES[state]}
        </p>
      )}

      <Tabs value={tab} onValueChange={(next) => setFilter(next as MapperFilter)}>
        <TabsList>
          <TabsTrigger value="all">
            All <Count n={shown.length} />
          </TabsTrigger>
          <TabsTrigger value="unmapped">
            Not mapped <Count n={counts.unmapped} />
          </TabsTrigger>
          <TabsTrigger value="mapped">
            Mapped <Count n={counts.mapped} />
          </TabsTrigger>
          {compared && (
            <TabsTrigger value="missing">
              Not in the newest request <Count n={counts.missing} />
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {shown.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="No keys yet"
          description={
            state === "gone" || state === "unreadable"
              ? "Add a key by hand below, or send this trigger a new request and its keys appear here."
              : "Send this trigger one request and its keys appear here, in the sender's order. Or add a key by hand below."
          }
          className="py-8"
        />
      ) : visible.length === 0 ? (
        <EmptyState icon={Search} title="No key matches" description="Try another word, or another tab." className="py-8" />
      ) : (
        <ul
          className="max-h-[32rem] divide-y divide-border overflow-y-auto rounded-md border border-border scrollbar-subtle"
          aria-label="Keys and the fields they fill"
        >
          {visible.map((row) => {
            const index = shown.indexOf(row);
            const suggested = row.suggestion ? titles.get(row.suggestion.field) : undefined;
            return (
              <li
                key={`${row.origin}:${row.nested ? "path" : "name"}:${row.key}`}
                // Fixed columns, so the arrows, the pickers and the actions line up down the list whatever each row holds.
                className="grid grid-cols-1 gap-x-3 gap-y-2 px-3 py-2 sm:grid-cols-[minmax(0,1fr)_1rem_minmax(0,1.3fr)_8.5rem] sm:items-center"
              >
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <code className="truncate font-mono text-xs text-text" title={row.key}>
                      {row.key}
                    </code>
                    {keyNotes(row.key).map((note) => (
                      <Badge key={note} tone="outline" size="sm">
                        {note}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-2xs text-text-subtle">
                    {row.origin === "request"
                      ? row.filled
                        ? "In the newest request"
                        : "In the newest request, empty"
                      : row.origin === "typed"
                        ? "Added by hand"
                        : compared
                          ? "Not in the newest request"
                          : "Saved with the trigger"}
                  </p>
                </div>
                <ArrowRight className="hidden size-4 justify-self-center text-text-subtle sm:block" aria-hidden />
                <div className="min-w-0">
                  {row.usedBy ? (
                    <p className="truncate text-xs text-text-muted">Read by the trigger as {row.usedBy}</p>
                  ) : (
                    <Combobox
                      modal
                      label={`Field for ${row.key}`}
                      options={options}
                      value={row.field}
                      onChange={(next) => change(pickField(shown, index, next, fields))}
                      placeholder={suggested ? `Suggested: ${suggested}` : "Choose a field"}
                      emptyText="No custom field on the contact form yet"
                    />
                  )}
                </div>
                <div className="flex h-8 items-center justify-end gap-1">
                  {row.field !== null ? (
                    <>
                      {compared && row.origin === "stored" && (
                        <Badge tone="warning" size="sm">
                          Not in request
                        </Badge>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={row.origin === "stored" ? `Remove ${row.key}` : `Clear the field for ${row.key}`}
                        onClick={() => change(clearRow(shown, index, fields))}
                      >
                        <X />
                      </Button>
                    </>
                  ) : row.suggestion ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      title={CONFIDENCE[row.suggestion.confidence]}
                      aria-label={`Use the suggested field for ${row.key}`}
                      onClick={() => change(pickField(shown, index, row.suggestion?.field ?? null, fields))}
                    >
                      Use suggestion
                    </Button>
                  ) : row.usedBy ? null : (
                    <Badge tone="outline" size="sm">
                      Not mapped
                    </Badge>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <Input
            aria-label="A key to add"
            autoComplete="off"
            className="font-mono"
            placeholder="A key the sender posts, like Date of Accident*"
            value={newKey}
            invalid={keyError !== null}
            onChange={(e) => {
              setNewKey(e.target.value);
              if (keyError) setKeyError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
          />
          {keyError && (
            <p role="alert" className="mt-1 text-xs text-danger-11">
              {keyError}
            </p>
          )}
        </div>
        <Button type="button" variant="outline" onClick={add}>
          <Plus />
          Add a key
        </Button>
      </div>
      <p className="text-xs text-text-muted">
        {plural(counts.mapped, "field")} mapped. Saved with the trigger. A field shown as{" "}
        <span className="font-mono">contact.phone_country</span> is stored as <span className="font-mono">phone_country</span>:
        the first part is the object, as GoHighLevel's Fields page writes it, and the folder is beside it.
      </p>
    </div>
  );
}

function Count({ n }: { n: number }) {
  return <span className="rounded bg-gray-3 px-1.5 text-2xs tabular-nums text-text-muted">{n}</span>;
}
